import { Component, OnInit, inject, signal } from '@angular/core';
import { BizEntityTypeCategory, IBizEntity, LoggerService } from '@optima-stride-ai/core';
import { FormBizEntityComponent } from '@optima-stride-ai/optima-app-framework';
import { closeOffCanvas, OffCanvasPlacement, OptimaOffCanvasService, ToastService } from '@optima-stride-ai/optima-components';
import { OffcanvasService } from '@coreui/angular';
import { GlobalStateService } from '../../../../../service/global.service';
import { AppStateService } from '../../../../services/state-store/app-state';
import { EntityFileUrlService } from '../../../../services/entity-file-url.service';
import { FileUploadService } from '../../../../services/file-upload.service';
import { FileExtensions, UIMessages } from '../../../../shared/enums/app-constants';

@Component({
  selector: 'app-form-save-as-template',
  imports: [FormBizEntityComponent],
  templateUrl: './form-save-as-template.component.html',
  styleUrls: ['./form-save-as-template.component.sass']
})
export class FormSaveAsTemplateComponent implements OnInit {
  private readonly logger = inject(LoggerService);
  private readonly global = inject(GlobalStateService);
  private readonly appStateService = inject(AppStateService);
  private readonly toastService = inject(ToastService);
  private readonly entityFileUrlService = inject(EntityFileUrlService);
  private readonly offCanvasFormService = inject(OptimaOffCanvasService);
  private readonly offcanvasService = inject(OffcanvasService);
  private readonly fileUploadService = inject(FileUploadService);

  public selectedBizEntityId: string | null = null;
  public bizEntity = signal<IBizEntity | null>(null);
  public isEditing = false;

  ngOnInit(): void {
    this.global.selectedBizEntityId$.subscribe((id) => {
      this.selectedBizEntityId = id;
    });

    const selectedEntity = this.appStateService.selectedBizEntityForEdit;
    if (selectedEntity) {
      this.initializeBizEntity(selectedEntity);
    } else {
      this.logger.warn('⚠️ No selected entity found for template creation');
    }
  }

  private initializeBizEntity(sourceEntity: IBizEntity): void {
    const templateEntity: IBizEntity = {
      id: '',
      name: sourceEntity.name,
      shortName: sourceEntity.shortName,
      description: sourceEntity.description,
      icon: sourceEntity.icon || 'cilLibrary',
      parentId: this.selectedBizEntityId || '',
      tenantId: '',
      appId: '',
      ownerId: '',
      draft: false,
      additionalInfo: [],
      archived: false,
      archivedBy: null,
      archivedOn: null,
      createdOn: new Date(),
      createdBy: null,
      type: sourceEntity.type,
      labels: [],
    };
    this.bizEntity.set(templateEntity);
    this.logger.info(`📄 Template form initialized for entity: ${sourceEntity.name}`);
  }

  /**
   * Map an entity category to its default file extension. Used as a fallback
   * when the source file URL has no extension (e.g. signed URLs that strip it).
   * Mirrors the mapping in `FileOperationsService.getFileExtensionFromCategory`.
   */
  private getExtensionForCategory(category: BizEntityTypeCategory | undefined): string {
    switch (category) {
      case BizEntityTypeCategory.HayagrivaFileDoc:
        return FileExtensions.DOCX;
      case BizEntityTypeCategory.HayagrivaFileSpreadsheet:
        return FileExtensions.XLSX;
      case BizEntityTypeCategory.HayagrivaFileCSV:
        return FileExtensions.CSV;
      case BizEntityTypeCategory.HayagrivaFilePresentation:
        return FileExtensions.PPTX;
      case BizEntityTypeCategory.HayagrivaFileDrawing:
        return FileExtensions.ODG;
      case BizEntityTypeCategory.HayagrivaFileMarkdown:
        return FileExtensions.MD;
      case BizEntityTypeCategory.HayagrivaFilePDF:
        return FileExtensions.PDF;
      default:
        return '';
    }
  }

  async onSaveRequested(params: { bizEntity: IBizEntity; isRelationship: boolean; relationship: any }): Promise<void> {
    const { bizEntity } = params;
    const selectedEntity = this.appStateService.selectedBizEntityForEdit;

    if (!bizEntity.name?.trim()) {
      this.toastService.toast('Please provide a template name.');
      return;
    }

    const folderId = bizEntity.parentId || this.selectedBizEntityId || selectedEntity?.parentId || '';
    if (!folderId) {
      this.toastService.toast('Error: No parent folder selected.');
      return;
    }

    const file = await this.entityFileUrlService.getFileUrl(selectedEntity?.id || '');

    if (!file?.fileUrl || file.fileUrl.length === 0) {
      this.logger.warn('⚠️ No file URL found for the selected entity');
      this.toastService.toast('Error: No file found for the selected entity.');
      return;
    }

    try {
      const fileUrl = file.fileUrl[0];
      const response = await fetch(fileUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      const blob = await response.blob();

      const templateName = bizEntity.name.trim();
      const urlParts = new URL(fileUrl);
      const pathParts = urlParts.pathname.split('/');
      const urlFileName = pathParts[pathParts.length - 1] || '';
      const urlExtension = urlFileName.includes('.')
        ? urlFileName.substring(urlFileName.lastIndexOf('.')).toLowerCase()
        : '';

      // Prefer the extension from the source file URL; fall back to the
      // category-derived extension when the URL omits it (e.g. signed URLs).
      const categoryExtension = this.getExtensionForCategory(selectedEntity?.type?.category);
      const finalExtension = urlExtension || categoryExtension;

      // Avoid double-appending if the user-entered name already ends with the
      // target extension (case-insensitive match).
      const alreadyHasExtension = !!finalExtension &&
        templateName.toLowerCase().endsWith(finalExtension.toLowerCase());
      const fileName = !finalExtension || alreadyHasExtension
        ? templateName
        : `${templateName}${finalExtension}`;

      const fileObject = new File([blob], fileName, { type: blob.type });
      const isTemplate = true;
      const uploadObservable = await this.fileUploadService.performDynamicUpload(folderId, [fileObject], isTemplate);

      uploadObservable.subscribe({
        next: (result) => {
          this.logger.info(`✅ File upload successful: ${JSON.stringify(result)}`);
          this.global.setBizEntityCreated(true);
          this.afterSubmissionSucceeded();
        },
        error: (error) => {
          this.logger.error(`❌ File upload failed: ${error}`);
          this.toastService.toast('Failed to upload template file. Please try again.');
        },
      });
    } catch (error) {
      this.handleSubmissionError(error);
    }
  }

  private afterSubmissionSucceeded(): void {
    closeOffCanvas(this.offCanvasFormService, this.offcanvasService, OffCanvasPlacement.End, true);
    this.toastService.toast('Template saved successfully.');
  }

  private handleSubmissionError(error: unknown): void {
    this.logger.error(`Error saving template: ${error}`);

    if (error instanceof Error && error.message === UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD) {
      this.toastService.toast(UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD);
      return;
    }

    this.toastService.toast('Failed to save template. Please try again.');
  }
}
